/**
 * InlineBehavioralChart Component
 * 
 * Displays a standardized 4-dot behavioral chart (DCTO traits) with style indicator.
 * 
 * Design Standard (DCTOS Framework):
 * - Aspect Ratio: 3:2 or fixed dimensions (compact)
 * - Dot Sizes: 7.8px (wide), 5.5px (card), 4px (compact)
 * - Colors: D=Red/Coral, C=Gold, T=Sky Blue, O=Mint, S=Gray
 * - Margins: 10px (wide), 8px (card), 5px (compact)
 * - Line Styles: 2px solid connecting lines, 1px dashed reference line
 * 
 * Usage:
 * - Wide mode: Desktop/horizontal displays with detailed analysis
 * - Compact mode: Mobile/vertical displays or inline card previews (rotated 90°)
 * 
 * @see src/lib/chart-constants.ts for complete design specifications
 */

import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, ReferenceLine } from 'recharts';
import { calculateBehavioralScores } from '@/utils/scoreCalculations';
import { useTheme } from 'next-themes';
import { Tooltip } from '@/components/ui/tooltip';
import { TRAIT_COLORS } from '@/lib/trait-colors';
import { getDctosGradient, DCTOS_GRADIENTS } from '@/lib/dctos-config';
import { 
  CHART_CONTAINER,
  CHART_CONTENT,
  CHART_DOT_SIZES,
  CHART_LINE_STYLES,
  CHART_MARGINS,
  OBJECTIVITY_SEPARATOR 
} from '@/lib/chart-constants';

interface InlineBehavioralChartProps {
  scores: any;
  comparisonScores?: any;
  size?: 'wide' | 'card' | 'compact' | 'mobile-card';
  assessmentVariant?: string;
}

const InlineBehavioralChart: React.FC<InlineBehavioralChartProps> = ({ scores, comparisonScores, size = 'compact', assessmentVariant }) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const [hoveredDot, setHoveredDot] = useState<{ trait: string; percentile: number } | null>(null);
  
  // Early return if no scores provided
  if (!scores) {
    return <div className="w-full h-24 flex items-center justify-center text-muted-foreground text-sm">-</div>;
  }

  // Check if this is M3 assessment - M3 stores percentiles directly
  const isM3Assessment = assessmentVariant === 'model3';

  // Calculate main scores with error handling
  let aPercentile, bPercentile, cPercentile, dPercentile, ePercentile;
  let objectivityModel1Percentile, objectivityModel2Percentile, objectivityModel3Percentile;
  
  try {
    // For M3, find where the percentiles are stored
    // Could be at top level, in selfBehavior.scores, or in scores.selfBehavior.scores
    let m3PercentileSource: any = null;
    if (isM3Assessment) {
      if (scores?.dominancePercentile !== undefined) {
        m3PercentileSource = scores;
      } else if (scores?.selfBehavior?.scores?.dominancePercentile !== undefined) {
        // Dual assessment - use selfBehavior for Natural Self chart
        m3PercentileSource = scores.selfBehavior.scores;
      } else if (scores?.scores?.dominancePercentile !== undefined) {
        m3PercentileSource = scores.scores;
      }
    }
    
    if (isM3Assessment && m3PercentileSource) {
      // M3: Use stored percentiles directly
      aPercentile = m3PercentileSource.dominancePercentile ?? 50;
      bPercentile = m3PercentileSource.extraversionPercentile ?? 50;
      cPercentile = m3PercentileSource.patiencePercentile ?? 50;
      dPercentile = m3PercentileSource.formalityPercentile ?? 50;
      ePercentile = m3PercentileSource.objectivityPercentile ?? 50;
      objectivityModel1Percentile = null;
      objectivityModel2Percentile = null;
      objectivityModel3Percentile = m3PercentileSource.objectivityPercentile ?? 50;
    } else {
      // M1/M2: Calculate percentiles from z-scores
      const calculatedScores = calculateBehavioralScores(scores);
      aPercentile = calculatedScores.aPercentile;
      bPercentile = calculatedScores.bPercentile;
      cPercentile = calculatedScores.cPercentile;
      dPercentile = calculatedScores.dPercentile;
      ePercentile = calculatedScores.ePercentile;
      objectivityModel1Percentile = calculatedScores.objectivityModel1Percentile;
      objectivityModel2Percentile = calculatedScores.objectivityModel2Percentile;
      objectivityModel3Percentile = calculatedScores.objectivityModel3Percentile;
    }
  } catch (error) {
    console.error('Failed to calculate behavioral scores:', error, scores);
    return <div className="w-full h-24 flex items-center justify-center text-muted-foreground text-sm">-</div>;
  }

  // Use the same objectivity priority system as the table
  const selectedObjectivityPercentile = objectivityModel3Percentile !== null ? objectivityModel3Percentile :
                                       (objectivityModel1Percentile !== null ? objectivityModel1Percentile :
                                       (objectivityModel2Percentile !== null ? objectivityModel2Percentile : ePercentile));

  // Check which traits cross the 50th percentile line compared to self assessment
  const isFlagged = (currentPercentile: number, comparisonPercentile: number) => {
    if (!comparisonScores) return false;
    
    // Only flag if one score is clearly above 50 and the other is clearly below 50
    // Scores exactly at 50 are considered neutral and don't trigger flags
    return (currentPercentile > 50 && comparisonPercentile < 50) || 
           (currentPercentile < 50 && comparisonPercentile > 50);
  };

  let comparisonPercentiles = { a: 50, b: 50, c: 50, d: 50, e: 50 };
  let comparisonSelectedObjectivity = 50;
  
  if (comparisonScores) {
    try {
      // For M3, use stored percentiles directly for comparison too
      if (isM3Assessment && comparisonScores?.dominancePercentile !== undefined) {
        comparisonPercentiles = {
          a: comparisonScores.dominancePercentile ?? 50,
          b: comparisonScores.extraversionPercentile ?? 50,
          c: comparisonScores.patiencePercentile ?? 50,
          d: comparisonScores.formalityPercentile ?? 50,
          e: comparisonScores.objectivityPercentile ?? 50
        };
        comparisonSelectedObjectivity = comparisonScores.objectivityPercentile ?? 50;
      } else {
        const comparison = calculateBehavioralScores(comparisonScores);
        comparisonPercentiles = {
          a: comparison.aPercentile,
          b: comparison.bPercentile,
          c: comparison.cPercentile,
          d: comparison.dPercentile,
          e: comparison.ePercentile
        };
        
        // Calculate comparison objectivity using same priority system
        comparisonSelectedObjectivity = comparison.objectivityModel3Percentile !== null ? comparison.objectivityModel3Percentile :
                                       (comparison.objectivityModel1Percentile !== null ? comparison.objectivityModel1Percentile :
                                       (comparison.objectivityModel2Percentile !== null ? comparison.objectivityModel2Percentile : comparison.ePercentile));
      }
    } catch (error) {
      console.error('Failed to calculate comparison scores:', error, comparisonScores);
      // Keep default comparison values on error
    }
  }

  // Style flagging: flag only if below 25th percentile (not crossing logic)
  const isObjectivityFlagged = selectedObjectivityPercentile < 25;

  const mainChartData = [
    { 
      name: 'D', 
      value: aPercentile,
      flagged: isFlagged(aPercentile, comparisonPercentiles.a),
      color: TRAIT_COLORS.dominance
    },
    { 
      name: 'C', 
      value: bPercentile,
      flagged: isFlagged(bPercentile, comparisonPercentiles.b),
      color: TRAIT_COLORS.extraversion
    },
    { 
      name: 'T', 
      value: cPercentile,
      flagged: isFlagged(cPercentile, comparisonPercentiles.c),
      color: TRAIT_COLORS.patience
    },
    { 
      name: 'O', 
      value: dPercentile,
      flagged: isFlagged(dPercentile, comparisonPercentiles.d),
      color: TRAIT_COLORS.formality
    }
  ];

  const isMobileCard = size === 'mobile-card';

  if (isMobileCard) {
    const mobileWidth = 48;
    const mobileHeight = 72;
    const traitStrokeWidth = 2.5;
    const traitRadius = CHART_DOT_SIZES['mobile-card'].traitDots;
    const traitInnerRadius = traitRadius * 0.67;
    const traitYPositions = [8, 22, 36, 50] as const;
    const trackY = 64;

    const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
    const safeInset = 1.25;

    const traitPlotLeft = traitRadius + traitStrokeWidth / 2 + safeInset;
    const traitPlotRight = mobileWidth - traitPlotLeft;
    const percentileToTraitX = (percentile: number) => {
      const clamped = clamp(percentile, 0, 100);
      return traitPlotLeft + (clamped / 100) * (traitPlotRight - traitPlotLeft);
    };

    const objectivityRadius = 3.4;
    const objectivityStroke = 1.5;
    const objectivityInnerRadius = 2.3;
    const objectivityPlotLeft = objectivityRadius + objectivityStroke / 2 + safeInset;
    const objectivityPlotRight = mobileWidth - objectivityPlotLeft;
    const objectivityX = selectedObjectivityPercentile !== null && selectedObjectivityPercentile !== undefined
      ? objectivityPlotLeft + (clamp(selectedObjectivityPercentile, 0, 100) / 100) * (objectivityPlotRight - objectivityPlotLeft)
      : null;

    const referenceX = percentileToTraitX(50);
    const mobileTraitPoints = mainChartData.map((trait, index) => {
      const gradient = getDctosGradient(trait.name as 'D' | 'C' | 'T' | 'O' | 'S');
      return {
        ...trait,
        x: percentileToTraitX(trait.value),
        y: traitYPositions[index],
        gradient,
        gradientId: `gradient-inline-mobile-${trait.name}-${Math.round(trait.value)}-${index}`,
      };
    });

    const objectivityGradientId = `gradient-inline-objectivity-mobile-${Math.round(selectedObjectivityPercentile ?? 50)}`;
    const polylinePoints = mobileTraitPoints.map(point => `${point.x},${point.y}`).join(' ');
    const connectingLineColor = 'hsl(var(--foreground))';
    const flaggedColor = '#F87171';

    return (
      <div className="w-full h-full overflow-hidden flex items-center justify-center" style={{ contain: 'strict' }}>
        <svg
          width={mobileWidth}
          height={mobileHeight}
          className="block"
          viewBox={`0 0 ${mobileWidth} ${mobileHeight}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ overflow: 'hidden' }}
        >
          <defs>
            {mobileTraitPoints.map(point => (
              <linearGradient key={point.gradientId} id={point.gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor={point.gradient[0]} />
                <stop offset="100%" stopColor={point.gradient[1]} />
              </linearGradient>
            ))}
            <linearGradient id={objectivityGradientId} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={DCTOS_GRADIENTS.style[0]} />
              <stop offset="100%" stopColor={DCTOS_GRADIENTS.style[1]} />
            </linearGradient>
          </defs>

          <line
            x1={referenceX}
            y1={traitYPositions[0]}
            x2={referenceX}
            y2={traitYPositions[traitYPositions.length - 1]}
            stroke={CHART_LINE_STYLES.reference.stroke}
            strokeWidth={CHART_LINE_STYLES.reference.strokeWidth}
            strokeDasharray={CHART_LINE_STYLES.reference.strokeDasharray}
          />

          <polyline
            points={polylinePoints}
            fill="none"
            stroke={connectingLineColor}
            strokeWidth={CHART_LINE_STYLES.connecting.strokeWidth}
            strokeLinejoin="round"
            strokeLinecap="round"
          />

          {mobileTraitPoints.map(point => {
            const stroke = point.flagged ? flaggedColor : `url(#${point.gradientId})`;
            const fill = point.flagged ? flaggedColor : `url(#${point.gradientId})`;

            return (
              <g key={`${point.name}-${point.y}`}>
                <circle
                  cx={point.x}
                  cy={point.y}
                  r={traitRadius}
                  fill="white"
                  stroke={stroke}
                  strokeWidth={traitStrokeWidth}
                />
                <circle
                  cx={point.x}
                  cy={point.y}
                  r={traitInnerRadius}
                  fill={fill}
                />
              </g>
            );
          })}

          <line
            x1={objectivityPlotLeft}
            y1={trackY}
            x2={objectivityPlotRight}
            y2={trackY}
            stroke="hsl(var(--border))"
            strokeWidth={1}
          />
          <line
            x1={(objectivityPlotLeft + objectivityPlotRight) / 2}
            y1={trackY - 2}
            x2={(objectivityPlotLeft + objectivityPlotRight) / 2}
            y2={trackY + 2}
            stroke={CHART_LINE_STYLES.reference.stroke}
            strokeWidth={1}
          />

          {objectivityX !== null && (
            <g>
              <circle
                cx={objectivityX}
                cy={trackY}
                r={objectivityRadius}
                fill="white"
                stroke={`url(#${objectivityGradientId})`}
                strokeWidth={objectivityStroke}
              />
              <circle
                cx={objectivityX}
                cy={trackY}
                r={objectivityInnerRadius}
                fill={`url(#${objectivityGradientId})`}
              />
            </g>
          )}
        </svg>
      </div>
    );
  }

  // Get dimensions based on size mode
  const container = CHART_CONTAINER[size];
  const dotSizes = CHART_DOT_SIZES[size];
  const margins = CHART_MARGINS[size];
  
  // Layout configuration based on size
  const isVerticalLayout = size === 'wide' || size === 'card';
  const shouldRotate = size === 'compact';
  const containerClasses = container.height ? `${container.width} ${container.height}` : container.width;
  const contentClasses = shouldRotate ? `${CHART_CONTENT.compact.width} ${CHART_CONTENT.compact.height}` : 'w-full h-full';
  const contentRotation = shouldRotate ? CHART_CONTENT.compact.rotation : 'none';

  const clampedObjectivityPercentile =
    selectedObjectivityPercentile !== null && selectedObjectivityPercentile !== undefined
      ? Math.min(97, Math.max(3, selectedObjectivityPercentile))
      : null;

  return (
    <div
      className={`${containerClasses} flex flex-col justify-center items-center relative overflow-hidden max-w-full`}
      style={container.aspectRatio ? { aspectRatio: container.aspectRatio } : undefined}
    >
      <div className={`${contentClasses} overflow-hidden`} style={{ transform: contentRotation }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={mainChartData}
            layout={isVerticalLayout ? 'vertical' : 'horizontal'}
            margin={margins}
          >
            {isVerticalLayout ? (
              <>
                <XAxis type="number" domain={[0, 100]} hide={true} />
                <YAxis type="category" dataKey="name" hide={true} />
              </>
            ) : (
              <YAxis type="number" domain={[0, 100]} hide={true} />
            )}
            <ReferenceLine
              x={isVerticalLayout ? 50 : undefined}
              y={isVerticalLayout ? undefined : 50}
              stroke={CHART_LINE_STYLES.reference.stroke}
              strokeWidth={CHART_LINE_STYLES.reference.strokeWidth}
              strokeDasharray={CHART_LINE_STYLES.reference.strokeDasharray}
            />
            <Line
              dataKey="value"
              stroke={isDark ? '#F1F5F9' : '#374151'}
              strokeWidth={CHART_LINE_STYLES.connecting.strokeWidth}
              dot={(props: any) => {
                const { cx, cy, payload } = props;
                const flaggedColor = '#F87171';
                const isFlaggedDot = payload.flagged;
                const gradient = getDctosGradient(payload.name as 'D' | 'C' | 'T' | 'O' | 'S');
                const gradientId = `gradient-inline-${payload.name}-${Math.round(cx)}-${Math.round(cy)}`;

                return (
                  <g
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={() => setHoveredDot({ trait: payload.name, percentile: payload.value })}
                    onMouseLeave={() => setHoveredDot(null)}
                  >
                    {!isFlaggedDot && (
                      <defs>
                        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor={gradient[0]} />
                          <stop offset="100%" stopColor={gradient[1]} />
                        </linearGradient>
                      </defs>
                    )}
                    <circle
                      cx={cx}
                      cy={cy}
                      r={dotSizes.traitDots}
                      fill="white"
                      stroke={isFlaggedDot ? flaggedColor : `url(#${gradientId})`}
                      strokeWidth={2.5}
                    />
                    <circle
                      cx={cx}
                      cy={cy}
                      r={dotSizes.traitDots * 0.67}
                      fill={isFlaggedDot ? flaggedColor : `url(#${gradientId})`}
                      strokeWidth={0}
                    />
                  </g>
                );
              }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      {/* Style dot positioned below the main chart at correct percentile position */}
      {selectedObjectivityPercentile !== null && selectedObjectivityPercentile !== undefined && clampedObjectivityPercentile !== null && (
        <div className={`relative ${shouldRotate ? 'w-12' : 'w-full'} mt-1 overflow-hidden`}>
          <div className="absolute top-1 left-0 w-full h-px border-t border-border" />
          <div
            className="absolute w-px h-1 transform -translate-x-1/2"
            style={{
              backgroundColor: isDark ? '#94A3B8' : '#6B7280',
              left: '50%',
              top: '2px'
            }}
          />
          <svg
            className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer"
            style={{
              left: `${clampedObjectivityPercentile}%`,
              top: '4px',
              width: size === 'compact' ? '6.8px' : '15.6px',
              height: size === 'compact' ? '6.8px' : '15.6px',
              overflow: 'visible'
            }}
            onMouseEnter={() => setHoveredDot({ trait: 'S', percentile: selectedObjectivityPercentile })}
            onMouseLeave={() => setHoveredDot(null)}
          >
            <defs>
              <linearGradient id="gradient-inline-objectivity" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor={DCTOS_GRADIENTS.style[0]} />
                <stop offset="100%" stopColor={DCTOS_GRADIENTS.style[1]} />
              </linearGradient>
            </defs>
            <circle
              cx="50%"
              cy="50%"
              r={size === 'compact' ? 3.4 : 7.8}
              fill="white"
              stroke="url(#gradient-inline-objectivity)"
              strokeWidth={size === 'compact' ? 1.5 : 2}
            />
            <circle
              cx="50%"
              cy="50%"
              r={size === 'compact' ? 2.3 : 5.2}
              fill="url(#gradient-inline-objectivity)"
            />
          </svg>
        </div>
      )}

      {/* Tooltip */}
      {hoveredDot && (
        <div
          className="absolute z-10 bg-popover text-popover-foreground px-2 py-1 rounded shadow-md text-xs pointer-events-none"
          style={{
            top: '-30px',
            left: '50%',
            transform: 'translateX(-50%)'
          }}
        >
          {hoveredDot.trait}: {hoveredDot.percentile}%
        </div>
      )}
    </div>
  );
};

export default InlineBehavioralChart;
